import { PhotoFlagStatus, PhotoRecord, Rating } from "@/lib/catalog"
import { replaceDialog } from "@/store/dialogs-state"
import { ConfirmDialog } from "./Dialogs/ConfirmDialog"
import { updateCustomPhotoOrdering } from "@/store/photo/photos-custom-order"
import {
	currentPhotoAtom,
	selectedExportablePhotosAtom,
	selectedPhotosAtom,
	selectedPhotosIDsAtom,
} from "@/store/photo/photos-state"
import {
	canHideSelectedPhotos,
	hideSelectedPhotos,
	rateSelectedPhotos,
	readXmpForSelectedPhotos,
	setColorTag,
	setFlag,
	unhideSelectedPhotos,
	updateXmpForSelectedPhotos,
} from "@/store/photo/store-photos"
import { pasteAdjustments, showCopySettings } from "@/store/store-history"
import {
	batchLock,
	batchMarkReference,
	batchRemoveReference,
	batchUnlock,
} from "@/store/style/reference"
import { Icon } from "./icons/Icon"
import { ExportDialog } from "./Dialogs/ExportDialog"
import { blockSampleProjectExportIfNeeded } from "@/store/project/project"
import {
	ContextMenuItem,
	ContextMenuSeparator,
	ContextMenuSubContent,
	ContextMenuSubTrigger,
	ContextMenuSub,
	ContextMenuPortal,
	ContextMenuCheckboxItem,
} from "./ui/ContextMenu"
import { isCullingModeAtom, projectAtom } from "@/store/project/projects-state"
import { useAtomValue } from "jotai"
import {
	createVirtualCopy,
	removeSelectedPhotos,
	deleteSelectedVirtualCopies,
} from "@/store/photo/store-photos"
import { PropsWithChildren, useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { AdjustTimeDialog } from "./Dialogs/AdjustTimeDialog"
import { commandOrCtrl } from "@/utils/system"
import { StarsView } from "./ui/Thumbnail"
import { ColorTag, ColorTagValues, CullingTag, CullingValues } from "@/utils/exif"
import {
	isCullingTagOptionEnabled,
	isShowingRejectsAtom,
	isShowingRejectsOrKeepersAtom,
	isSuggestingKeepersAtom,
	setManualCullingTagForPhotos,
} from "@/store/importing/culling-state"
import {
	actionShortcuts,
	colorKeyToValue,
	eventToActionKey,
	cullingKeyToValue,
	flagKeyToValue,
	ratingKeyToValue,
} from "@/shortcuts/shortcuts"
import { PublishDialog } from "./Dialogs/PublishDialog"
import { isUserPartiallyInactive } from "@/api/dto/users"
import toast from "react-hot-toast"
import { showAccountPartiallyInactiveDialog } from "./Dialogs/SubscriptionWarningDialogs"
import { useUserQuery } from "@/api/users-api"
import { colorTagKeys, photoFlagKeys, cullingTagKeys } from "@/i18n/dual-role-strings"

const allowedShortcuts = [
	actionShortcuts.rate0,
	actionShortcuts.rate1,
	actionShortcuts.rate2,
	actionShortcuts.rate3,
	actionShortcuts.rate4,
	actionShortcuts.rate5,
	actionShortcuts.colorTagRed,
	actionShortcuts.colorTagYellow,
	actionShortcuts.colorTagGreen,
	actionShortcuts.colorTagBlue,
	actionShortcuts.flagged,
	actionShortcuts.unflagged,
	actionShortcuts.rejected,
	actionShortcuts.cullingCandidate,
	actionShortcuts.cullingKeeper,
	actionShortcuts.cullingReject,
	actionShortcuts.cullingClear,
] as const

const allowedShortcutsSet = new Set<string>(allowedShortcuts)

export const PhotoContextMenu = (props: PropsWithChildren<{ photo?: PhotoRecord }>) => {
	const { t } = useTranslation("components")
	const project = useAtomValue(projectAtom)

	const selectedPhotoIDs = useAtomValue(selectedPhotosIDsAtom)
	const selectedPhotos = useAtomValue(selectedPhotosAtom)
	const selectedExportablePhotos = useAtomValue(selectedExportablePhotosAtom)
	const currentPhoto = useAtomValue(currentPhotoAtom)
	const isCullingMode = useAtomValue(isCullingModeAtom)
	const isManualEditing = project?.isManual
	const isShowingRejectsOrKeepers = useAtomValue(isShowingRejectsOrKeepersAtom)
	const isShowingKeepers = useAtomValue(isSuggestingKeepersAtom)
	const isShowingRejects = useAtomValue(isShowingRejectsAtom)
	const photo = props.photo ?? currentPhoto

	const [manualCullingTag, setManualCullingTag] = useState<CullingTag | null>(
		photo?.manualCullingTag ?? null,
	)
	const [localColorTag, setLocalColorTag] = useState<ColorTag>(photo?.colorTag ?? "No Color") // i18n-ignore
	const [localRating, setLocalRating] = useState<Rating>(photo?.rating ?? 0)
	const [localFlag, setLocalFlag] = useState<PhotoFlagStatus>(photo?.flag ?? "unflagged")

	const copyAdjustmentsRef = useRef<HTMLDivElement | null>(null)
	const pasteAdjustmentsRef = useRef<HTMLDivElement | null>(null)
	const markReferenceRef = useRef<HTMLDivElement | null>(null)
	const removeReferenceRef = useRef<HTMLDivElement | null>(null)
	const lockPhotoRef = useRef<HTMLDivElement | null>(null)
	const unlockPhotoRef = useRef<HTMLDivElement | null>(null)
	const hidePhotoRef = useRef<HTMLDivElement | null>(null)
	const unhidePhotoRef = useRef<HTMLDivElement | null>(null)
	const deletePhotoRef = useRef<HTMLDivElement | null>(null)
	const deleteVirtualCopyRef = useRef<HTMLDivElement | null>(null)

	const isPrimarySelected = currentPhoto?.id === photo?.id
	const isSecondarySelected = photo && selectedPhotoIDs?.has(photo?.id)
	const isSelected = isPrimarySelected || isSecondarySelected

	const userQuery = useUserQuery()
	const user = userQuery.data

	const virtualCopiesCount = selectedPhotos.reduce((count, photo) => {
		return photo.parentId !== null ? count + 1 : count
	}, 0)

	const focusAndClick = <T extends HTMLElement>(ref: React.RefObject<T | null>) => {
		const el = ref.current
		if (!el || el.matches('[aria-disabled="true"]')) return

		el.focus()

		// Ensure that the focus state is observable.
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				el.click()
			})
		})
	}

	const isCullingTagAvailable = useCallback(
		(tag: CullingTag | null) =>
			isCullingTagOptionEnabled({
				tag,
				isShowingRejectsOrKeepers,
				isShowingKeepers,
				isShowingRejects,
			}),
		[isShowingRejectsOrKeepers, isShowingKeepers, isShowingRejects],
	)

	const saveXmpFiles = async () => {
		const isPlural = selectedPhotos.length > 1

		try {
			await updateXmpForSelectedPhotos()
			replaceDialog(ConfirmDialog, {
				title: t("photoContextMenu.dialogSaved"),
				message: t("photoContextMenu.xmpFileUpdated", { count: isPlural ? 2 : 1 }),
				primary: { title: t("common:action.ok") },
				trackingViewName: "xmp_save_success",
			})
		} catch (error) {
			replaceDialog(ConfirmDialog, {
				title: t("photoContextMenu.dialogSaveFailed"),
				message: t("photoContextMenu.xmpSaveFailed", { count: isPlural ? 2 : 1 }),
				primary: { title: t("common:action.ok") },
				trackingViewName: "xmp_save_error",
			})
		}
	}

	const readXmpFiles = async () => {
		const isPlural = selectedPhotos.length > 1

		try {
			await readXmpForSelectedPhotos()
			replaceDialog(ConfirmDialog, {
				title: t("photoContextMenu.dialogReadComplete"),
				message: t("photoContextMenu.xmpReadSuccess", { count: isPlural ? 2 : 1 }),
				primary: { title: t("common:action.ok") },
				trackingViewName: "xmp_read_success",
			})
		} catch (error) {
			replaceDialog(ConfirmDialog, {
				title: t("photoContextMenu.dialogReadFailed"),
				message: t("photoContextMenu.xmpReadFailed", { count: isPlural ? 2 : 1 }),
				primary: { title: t("common:action.ok") },
				trackingViewName: "xmp_read_error",
			})
		}
	}

	useEffect(() => {
		setManualCullingTag(photo?.manualCullingTag ?? null)
		setLocalColorTag(photo?.colorTag ?? "No Color") // i18n-ignore
		setLocalRating(photo?.rating ?? 0)
		setLocalFlag(photo?.flag ?? "unflagged")
	}, [photo])

	useEffect(() => {
		const handleCullingShortcuts = (shortcut: string) => {
			if (
				isCullingMode &&
				(shortcut === actionShortcuts.cullingClear ||
					Object.keys(cullingKeyToValue).includes(shortcut))
			) {
				const value = cullingKeyToValue[shortcut as keyof typeof cullingKeyToValue] ?? null
				if (!isCullingTagAvailable(value)) {
					return false
				}
				void setManualCullingTagForPhotos([...selectedPhotoIDs], value).then(() => {
					setManualCullingTag(value)
				})
				return true
			}

			return false
		}

		const handleColorShortcuts = (shortcut: string) => {
			if (Object.keys(colorKeyToValue).includes(shortcut)) {
				const value = colorKeyToValue[shortcut]
				setColorTag(value)
				setLocalColorTag(value)
				return true
			}

			return false
		}

		const handleRatingShortcuts = (shortcut: string) => {
			if (Object.keys(ratingKeyToValue).includes(shortcut)) {
				const value = ratingKeyToValue[shortcut] as Rating
				rateSelectedPhotos(value)
				setLocalRating(value)
				return true
			}

			return false
		}

		const handleFlagShortcuts = (shortcut: string) => {
			if (Object.keys(flagKeyToValue).includes(shortcut)) {
				const value = flagKeyToValue[shortcut]
				setFlag([...selectedPhotoIDs], value)
				setLocalFlag(value)
				return true
			}

			return false
		}

		const onKeyDown = (e: KeyboardEvent) => {
			if (e.isComposing) return

			const key = e.key.toLowerCase()
			if (["meta", "control", "shift", "alt"].includes(key)) {
				return
			}

			const shortcut = eventToActionKey(e)

			let handled = false

			switch (shortcut) {
				case actionShortcuts.copyAdjustments:
					focusAndClick(copyAdjustmentsRef)
					handled = true
					break

				case actionShortcuts.pasteAdjustments:
					focusAndClick(pasteAdjustmentsRef)
					handled = true
					break

				case actionShortcuts.markReference:
					focusAndClick(markReferenceRef)
					handled = true
					break

				case actionShortcuts.removeReference:
					focusAndClick(removeReferenceRef)
					handled = true
					break

				case actionShortcuts.lockPhoto:
					focusAndClick(lockPhotoRef)
					handled = true
					break

				case actionShortcuts.unlockPhoto:
					focusAndClick(unlockPhotoRef)
					handled = true
					break

				case actionShortcuts.hidePhoto:
					focusAndClick(hidePhotoRef)
					handled = true
					break

				case actionShortcuts.unhidePhoto:
					focusAndClick(unhidePhotoRef)
					handled = true
					break

				case actionShortcuts.delete:
				case actionShortcuts.del:
					if (virtualCopiesCount === 0) {
						focusAndClick(deletePhotoRef)
					} else {
						focusAndClick(deleteVirtualCopyRef)
					}
					handled = true
					break

				default:
					break
			}

			if (!handled && allowedShortcutsSet.has(shortcut)) {
				handled =
					handleCullingShortcuts(shortcut) ||
					handleColorShortcuts(shortcut) ||
					handleRatingShortcuts(shortcut) ||
					handleFlagShortcuts(shortcut)
			}

			e.preventDefault()
			e.stopPropagation()
		}

		window.addEventListener("keydown", onKeyDown, true)
		return () => window.removeEventListener("keydown", onKeyDown, true)
	}, [selectedPhotoIDs, virtualCopiesCount, isCullingMode, isCullingTagAvailable])

	const canReorder = (() => {
		if (isSelected) {
			return false
		} else if (selectedPhotoIDs.size === 0) {
			return false
		} else {
			return true
		}
	})()

	const createVirtualCopies = () => {
		for (const photo of selectedPhotos) {
			void createVirtualCopy(photo)
		}
	}

	const renderShortcutKeys = (keys: string) => {
		return <div className="grow text-right">{keys}</div>
	}

	const renderColorShortcutKeys = (color: ColorTag) => {
		switch (color) {
			case "Red":
				return renderShortcutKeys("6")
			case "Yellow":
				return renderShortcutKeys("7")
			case "Green":
				return renderShortcutKeys("8")
			case "Blue":
				return renderShortcutKeys("9")
			case "Purple":
				return null
			case "No Color": // i18n-ignore
				return renderShortcutKeys(`${commandOrCtrl} 0`)
		}
	}

	const renderFlagShortcutKeys = (flag: PhotoFlagStatus) => {
		switch (flag) {
			case "rejected":
				return renderShortcutKeys("X")
			case "flagged":
				return renderShortcutKeys("P")
			case "unflagged":
				return renderShortcutKeys("U")
		}
	}

	const renderCullingTagShortcutKeys = (tag: CullingTag) => {
		switch (tag) {
			case "Reject":
				return renderShortcutKeys("R")
			case "Candidate":
				return renderShortcutKeys("C")
			case "Keeper":
				return renderShortcutKeys("T")
		}
	}

	const renderCopyPasteItems = () => {
		return (
			<>
				<ContextMenuItem
					ref={copyAdjustmentsRef}
					trackingLabel="copy_edits"
					aria-label={t("photoContextMenu.ariaCopyPhotoEdits")}
					onSelect={showCopySettings}
					disabled={!isSelected || selectedPhotoIDs.size > 1}
				>
					<div>{t("photoContextMenu.copyEdits")}</div>
					{renderShortcutKeys(`${commandOrCtrl} C`)}
				</ContextMenuItem>
				<ContextMenuItem
					ref={pasteAdjustmentsRef}
					trackingLabel="paste_edits"
					aria-label={t("photoContextMenu.ariaPastePhotoEdits")}
					onSelect={pasteAdjustments}
					disabled={!isSelected}
				>
					{t("photoContextMenu.pasteEdits", { count: selectedPhotos.length })}
					{renderShortcutKeys(`${commandOrCtrl} V`)}
				</ContextMenuItem>

				<ContextMenuSeparator />
			</>
		)
	}

	const renderReferenceAndLockItems = () => {
		return (
			<>
				<ContextMenuItem
					trackingLabel="batch_mark_reference"
					aria-label={t("photoContextMenu.ariaMarkPhotoAsReference")}
					trackingValues={{
						projectId: project?.id,
						photoCount: selectedPhotos.filter((p) => p.kind != "reference").length,
					}}
					onSelect={batchMarkReference}
					ref={markReferenceRef}
					disabled={!isSelected || selectedPhotos.every((p) => p.kind === "reference")}
				>
					{t("photoContextMenu.markReference", { count: selectedPhotos.length })}
					{renderShortcutKeys("M")}
				</ContextMenuItem>
				<ContextMenuItem
					trackingLabel="batch_remove_reference"
					aria-label={t("photoContextMenu.ariaRemovePhotoReference")}
					trackingValues={{
						projectId: project?.id,
						photoCount: selectedPhotos.filter((p) => p.kind === "reference").length,
					}}
					onSelect={batchRemoveReference}
					ref={removeReferenceRef}
					disabled={!isSelected || selectedPhotos.every((p) => p.kind !== "reference")}
				>
					{t("photoContextMenu.removeReference", { count: selectedPhotos.length })}
					{renderShortcutKeys(`${commandOrCtrl} M`)}
				</ContextMenuItem>
				<ContextMenuItem
					trackingLabel="lock_photo"
					aria-label={t("photoContextMenu.ariaLockPhotoEditing")}
					onSelect={batchLock}
					disabled={
						!isSelected ||
						selectedPhotos.every((p) => p.kind === "locked" || p.kind === "reference")
					}
					ref={lockPhotoRef}
				>
					{t("photoContextMenu.lockPhoto", { count: selectedPhotos.length })}
					{renderShortcutKeys("L")}
				</ContextMenuItem>
				<ContextMenuItem
					trackingLabel="unlock_photo"
					aria-label={t("photoContextMenu.ariaUnlockPhotoEditing")}
					onSelect={batchUnlock}
					ref={unlockPhotoRef}
					disabled={!isSelected || selectedPhotos.every((p) => p.kind !== "locked")}
				>
					{t("photoContextMenu.unlockPhoto", { count: selectedPhotos.length })}
					{renderShortcutKeys(`${commandOrCtrl} L`)}
				</ContextMenuItem>

				<ContextMenuSeparator />
			</>
		)
	}

	const renderVirtualCopyItems = () => {
		return (
			<>
				<ContextMenuItem
					trackingLabel="create_virtual_copy"
					aria-label={t("photoContextMenu.ariaCreatePhotoVirtualCopy")}
					onSelect={createVirtualCopies}
					disabled={!isSelected || virtualCopiesCount > 0}
				>
					{t("photoContextMenu.createVirtualCopy", { count: selectedPhotos.length })}
				</ContextMenuItem>
				<ContextMenuItem
					trackingLabel="delete_virtual_copy"
					aria-label={t("photoContextMenu.ariaDeletePhotoVirtualCopy")}
					onSelect={deleteSelectedVirtualCopies}
					disabled={!isSelected || virtualCopiesCount !== selectedPhotos.length}
					ref={deleteVirtualCopyRef}
				>
					{t("photoContextMenu.removeVirtualCopy", { count: selectedPhotos.length })}
					{renderShortcutKeys("Delete")}
				</ContextMenuItem>

				<ContextMenuSeparator />
			</>
		)
	}

	const renderRatingItems = () => {
		return (
			<>
				<ContextMenuSub>
					<ContextMenuSubTrigger disabled={!isSelected}>
						{t("photoContextMenu.setFlag")}
					</ContextMenuSubTrigger>
					<ContextMenuPortal>
						<ContextMenuSubContent sideOffset={2} alignOffset={-5}>
							{(["flagged", "unflagged", "rejected"] as PhotoFlagStatus[]).map(
								(flag) => (
									<ContextMenuCheckboxItem
										key={flag}
										trackingLabel="set_flag"
										aria-label={t("photoContextMenu.ariaSetPhotoFlag")}
										trackingValue={flag}
										checked={localFlag === flag}
										onSelect={() => {
											setFlag([...selectedPhotoIDs], flag)
											setLocalFlag(flag)
										}}
									>
										<div className="grow">{t(photoFlagKeys[flag])}</div>
										{renderFlagShortcutKeys(flag)}
									</ContextMenuCheckboxItem>
								),
							)}
						</ContextMenuSubContent>
					</ContextMenuPortal>
				</ContextMenuSub>

				<ContextMenuSub>
					<ContextMenuSubTrigger disabled={!isSelected}>
						{t("photoContextMenu.setRating")}
					</ContextMenuSubTrigger>
					<ContextMenuPortal>
						<ContextMenuSubContent sideOffset={2} alignOffset={-5}>
							{([0, 1, 2, 3, 4, 5] as Rating[]).map((rating) => (
								<ContextMenuCheckboxItem
									key={rating}
									trackingLabel="set_rating"
									aria-label={t("photoContextMenu.ariaSetPhotoRating")}
									trackingValue={String(rating)}
									checked={localRating === rating}
									onSelect={() => {
										rateSelectedPhotos(rating)
										setLocalRating(rating)
									}}
								>
									{rating === 0 && t("photoContextMenu.none")}
									{rating !== 0 && <StarsView count={rating} size={12} />}
									{renderShortcutKeys(String(rating))}
								</ContextMenuCheckboxItem>
							))}
						</ContextMenuSubContent>
					</ContextMenuPortal>
				</ContextMenuSub>

				<ContextMenuSub>
					<ContextMenuSubTrigger disabled={!isSelected}>
						{t("photoContextMenu.setColorLabel")}
					</ContextMenuSubTrigger>
					<ContextMenuPortal>
						<ContextMenuSubContent sideOffset={2} alignOffset={-5}>
							{ColorTagValues.map((color) => (
								<ContextMenuCheckboxItem
									key={color}
									trackingLabel="set_color_tag"
									aria-label={t("photoContextMenu.ariaSetPhotoColorTag")}
									trackingValue={color}
									checked={localColorTag === color}
									onSelect={() => {
										setColorTag(color)
										setLocalColorTag(color)
									}}
								>
									<div className="grow">{t(colorTagKeys[color])}</div>
									{renderColorShortcutKeys(color)}
								</ContextMenuCheckboxItem>
							))}
						</ContextMenuSubContent>
					</ContextMenuPortal>
				</ContextMenuSub>

				{isCullingMode && (
					<ContextMenuSub>
						<ContextMenuSubTrigger disabled={!isSelected}>
							{t("photoContextMenu.overrideAiCulling")}
						</ContextMenuSubTrigger>
						<ContextMenuPortal>
							<ContextMenuSubContent sideOffset={2} alignOffset={-5}>
								<ContextMenuCheckboxItem
									key={"Auto"}
									trackingLabel="set_manual_culling_tag"
									trackingValue={"Auto"}
									checked={manualCullingTag === null}
									disabled={!isShowingRejectsOrKeepers}
									onSelect={() => {
										void setManualCullingTagForPhotos(
											[...selectedPhotoIDs],
											null,
										).then(() => setManualCullingTag(null))
									}}
								>
									<div className="grow">{t("photoContextMenu.auto")}</div>
									{renderShortcutKeys("A")}
								</ContextMenuCheckboxItem>
								{CullingValues.map((value) => (
									<ContextMenuCheckboxItem
										key={value}
										trackingLabel="set_manual_culling_tag"
										trackingValue={value}
										checked={manualCullingTag === value}
										disabled={!isCullingTagAvailable(value)}
										onSelect={() => {
											void setManualCullingTagForPhotos(
												[...selectedPhotoIDs],
												value,
											).then(() => setManualCullingTag(value))
										}}
									>
										<div className="grow">{t(cullingTagKeys[value])}</div>
										{renderCullingTagShortcutKeys(value)}
									</ContextMenuCheckboxItem>
								))}
							</ContextMenuSubContent>
						</ContextMenuPortal>
					</ContextMenuSub>
				)}

				<ContextMenuSeparator />

				<ContextMenuSub>
					<ContextMenuSubTrigger disabled={!isSelected}>
						{t("photoContextMenu.metadata")}
					</ContextMenuSubTrigger>
					<ContextMenuPortal>
						<ContextMenuSubContent sideOffset={2} alignOffset={-5}>
							<ContextMenuItem
								key={"save_xmp"}
								trackingLabel="update_xmp"
								aria-label={t("photoContextMenu.ariaUpdatePhotoXmpMetadata")}
								trackingValue={"save_xmp"}
								onSelect={() => void saveXmpFiles()}
								disabled={
									!isSelected ||
									selectedPhotos.length === 0 ||
									selectedPhotos.some((photo) => photo.parentId)
								}
							>
								{t("photoContextMenu.saveXmp", { count: selectedPhotos.length })}
							</ContextMenuItem>
							<ContextMenuItem
								key={"read_xmp"}
								trackingLabel="read_xmp"
								aria-label={t("photoContextMenu.ariaReadPhotoXmpMetadata")}
								trackingValue={"read_xmp"}
								onSelect={() => void readXmpFiles()}
								disabled={
									!isSelected ||
									selectedPhotos.length === 0 ||
									selectedPhotos.some((photo) => photo.parentId)
								}
							>
								{t("photoContextMenu.readXmp", { count: selectedPhotos.length })}
							</ContextMenuItem>
						</ContextMenuSubContent>
					</ContextMenuPortal>
				</ContextMenuSub>

				<ContextMenuSeparator />
			</>
		)
	}

	const renderRemoveAndHideItems = () => {
		return (
			<>
				<ContextMenuItem
					trackingLabel="remove_photo"
					aria-label={t("photoContextMenu.ariaRemovePhoto")}
					onSelect={removeSelectedPhotos}
					disabled={
						!isSelected ||
						selectedPhotos.some((p) => p.kind === "reference") ||
						virtualCopiesCount > 0
					}
					ref={deletePhotoRef}
				>
					{t("photoContextMenu.removePhoto", { count: selectedPhotos.length })}
					{renderShortcutKeys("Delete")}
				</ContextMenuItem>
				<ContextMenuItem
					trackingLabel="hide_photo"
					aria-label={t("photoContextMenu.ariaHidePhoto")}
					onSelect={() => void hideSelectedPhotos()}
					disabled={!isSelected || !canHideSelectedPhotos(selectedPhotos)}
					ref={hidePhotoRef}
				>
					{t("photoContextMenu.hidePhoto", { count: selectedPhotos.length })}
					{renderShortcutKeys("H")}
				</ContextMenuItem>
				<ContextMenuItem
					trackingLabel="unhide_photo"
					aria-label={t("photoContextMenu.ariaUnhidePhoto")}
					onSelect={() => void unhideSelectedPhotos()}
					disabled={!isSelected || selectedPhotos.every((p) => !p.isHidden)}
					ref={unhidePhotoRef}
				>
					{t("photoContextMenu.unhidePhoto", { count: selectedPhotos.length })}
					{renderShortcutKeys(`${commandOrCtrl} H`)}
				</ContextMenuItem>

				<ContextMenuSeparator />
			</>
		)
	}

	const renderExportAndPublish = () => {
		return (
			<>
				<ContextMenuItem
					trackingLabel="export"
					onSelect={() => {
						if (project?.isSampleProject && blockSampleProjectExportIfNeeded()) {
							return
						}
						replaceDialog(ExportDialog)
					}}
					disabled={!isSelected || selectedExportablePhotos.length === 0}
				>
					{t("photoContextMenu.exportPhoto", { count: selectedExportablePhotos.length })}
				</ContextMenuItem>

				<ContextMenuItem
					trackingLabel="publish"
					onSelect={() => {
						if (!user) {
							toast.error(t("photoContextMenu.couldNotLoadYourAccount"))
							return
						}

						if (isUserPartiallyInactive(user)) {
							showAccountPartiallyInactiveDialog()
							return
						}
						replaceDialog(PublishDialog)
					}}
					disabled={!isSelected || selectedExportablePhotos.length === 0}
				>
					{t("photoContextMenu.publishPhoto", { count: selectedExportablePhotos.length })}
				</ContextMenuItem>

				<ContextMenuSeparator />
			</>
		)
	}

	const renderTimeAndOrderItems = () => {
		return (
			<>
				<ContextMenuItem
					trackingLabel="adjust_capture_time"
					aria-label={t("photoContextMenu.ariaAdjustPhotoCaptureTime")}
					onSelect={() => {
						const photo = selectedPhotos[0]
						if (photo) {
							replaceDialog(AdjustTimeDialog, { anchorPhoto: photo })
						}
					}}
					disabled={!isSelected || selectedPhotos.length === 0}
				>
					{t("photoContextMenu.adjustCaptureTime")}
				</ContextMenuItem>

				<ContextMenuSeparator />

				<ContextMenuItem
					trackingLabel="move_left"
					aria-label={t("photoContextMenu.ariaMoveSelectedPhotosLeft")}
					disabled={!canReorder || !photo}
					onSelect={() => updateCustomPhotoOrdering(photo as PhotoRecord, "before")}
				>
					<Icon size={16} className="mr-2" name="move_left" />{" "}
					{t("photoContextMenu.move")}{" "}
					{selectedPhotoIDs.size > 0 ? selectedPhotoIDs.size : ""}{" "}
					{t("photoContextMenu.selectedPhoto")}
					{selectedPhotoIDs.size === 1 ? "" : "s"} {t("photoContextMenu.toTheLeft")}
				</ContextMenuItem>
				<ContextMenuItem
					trackingLabel="move_right"
					aria-label={t("photoContextMenu.ariaMoveSelectedPhotosRight")}
					disabled={!canReorder || !photo}
					onSelect={() => updateCustomPhotoOrdering(photo as PhotoRecord, "after")}
				>
					<Icon size={16} className="mr-2" name="move_right" />{" "}
					{t("photoContextMenu.move")}{" "}
					{selectedPhotoIDs.size > 0 ? selectedPhotoIDs.size : ""}{" "}
					{t("photoContextMenu.selectedPhoto")}
					{selectedPhotoIDs.size === 1 ? "" : "s"} {t("photoContextMenu.toTheRight")}
				</ContextMenuItem>
			</>
		)
	}

	const renderCullingMenuItems = () => {
		return (
			<div aria-label={t("photoContextMenu.ariaPhotoContextMenu")}>
				{renderRatingItems()}
				{renderVirtualCopyItems()}
				{renderRemoveAndHideItems()}
				{renderTimeAndOrderItems()}
			</div>
		)
	}

	const renderSmartEditingMenuItems = () => {
		return (
			<div aria-label={t("photoContextMenu.ariaPhotoContextMenu")}>
				{renderCopyPasteItems()}
				{renderReferenceAndLockItems()}
				{renderRatingItems()}
				{renderVirtualCopyItems()}
				{renderRemoveAndHideItems()}
				{renderExportAndPublish()}
				{renderTimeAndOrderItems()}
			</div>
		)
	}

	const renderManualEditingMenuItems = () => {
		return (
			<div aria-label={t("photoContextMenu.ariaPhotoContextMenu")}>
				{renderCopyPasteItems()}
				{renderRatingItems()}
				{renderVirtualCopyItems()}
				{renderRemoveAndHideItems()}
				{renderExportAndPublish()}
				{renderTimeAndOrderItems()}
			</div>
		)
	}

	if (isCullingMode) {
		return renderCullingMenuItems()
	} else if (isManualEditing) {
		return renderManualEditingMenuItems()
	} else {
		return renderSmartEditingMenuItems()
	}
}
